'use strict';
/* ==========================================================================
   core-local/civic.js — the civics "print shop" look (DESIGN.md §2), drawn through the collage engine.
   lessons/electoral: an UNCHANGED COPY of lessons/ratify/core-local/civic.js (no shared edit; the ink colours of the two
   tickets live in core-local/mapkit.js MapKit.INK). Its own history: a COPY of
   lessons/free-speech/core-local/civic.js (itself a copy of lessons/branches'; no shared edit) for "Federalists vs
   Anti-Federalists" (ratify). Unchanged except Civic.SIDE at the end (the two sides' colours, DESIGN.md §2).
   Every piece is a cached shape (paperPiece / paperRect / paperCircle with fixed seeds and fixed point lists, baked
   once into sprites by the engine); animation is by transform (rotate / scale / translate), never a new shape per frame,
   and no shadowOn(): the paper pieces bring their own pre-baked shadows.

   Civic.C                       colours: navy #1f2f55, brick #9e2b25, cream #f3ead7, gold #b08a2e (+ parchment, wax)
   Civic.BRANCH[b]               b = legislative | executive | judicial → {color, ink, building, tool, label, job}
                                 legislative = navy + the Capitol + a quill; executive = brick + the White House + a pen;
                                 judicial = old gold + the Court + a gavel (the same everywhere in the lesson)
   Civic.bunting(x0, x1, y, o)   a red-white-blue paper bunting strip on a sagging string {sag, n, seed, t0, sway}
   Civic.seal(x, y, r, o)        a red wax seal with ribbon tails {text, seed, s, rot, ribbon}
   Civic.woodtype(str, x, y, size, o)  woodtype letters, each on its own block {color, ink, t0, seed, align, gap,
                                 stagger, rot} → total width (letters pop in; a letter's block is a cached sprite)
   Civic.masthead(title, x, y, w, o)   a newspaper masthead: woodtype title between double rules {date, price, t0}
   Civic.building(kind, x, y, h, o)    kind = capitol | whitehouse | court: the engraving cut-out (IMG[kind]) when the
                                 art is in, else a paper stand-in in the branch colour; x, y = the base centre
   Civic.tool(kind, x, y, h, o)  quill | pen | gavel (IMG when present, else ink + paper doodles)
   Civic.parchment(x, y, w, h, o) a parchment sheet (cream paper, torn edges, faint ruled lines) {seed, rot, lines, depth}

   ---- free-speech additions ----
   Civic.seal(x, y, r, {sub})    + an optional second, smaller line under the text (a docket seal: year over vote)
   Civic.FREEDOM[id]             the five freedoms, the same colour + icon everywhere in the lesson (DESIGN.md §2):
                                 religion = plum + a candle, speech = navy + a speech bubble, press = brick + a newspaper,
                                 assembly = green + people, petition = gold + a quill and scroll → {color, ink, label, icon}
   Civic.FREEDOMS                ['religion', 'speech', 'press', 'assembly', 'petition'] (First Amendment order)
   Civic.icon(kind, x, y, size, o)   the icon alone in ink colour o.color (candle | bubble | newspaper | people | quill),
                                 cached paper pieces {s, rot, seed, color}
   Civic.freedom(id, x, y, size, o)  a freedom badge: a paper disc in its colour + its icon + (o.label) a printed slab-type tag
                                 under it, shrunk to the badge width {s, rot, seed, label, t0}
   Civic.prop(kind, x, y, h, o)  the new engraving props (armband | newspaper | megaphone | protest_sign | scroll_rights |
                                 microphone | printing_press): the cut-out IMG[kind] when the assets agent's art is in,
                                 else a paper stand-in {s, rot, seed, ax, ay, flip}
   Civic.docket(x, y, w, h, o)   a court docket sheet: ruled parchment, "DOCKET" rule, the case name in slab type, a
                                 wax seal with the year over the vote, an EOC badge when o.eoc {name, year, vote, eoc,
                                 lines: [text...], s, rot, seed, t0}

   ---- ratify addition ----
   Civic.SIDE[side]              side = federalist | anti (core-local/ratify.js): the colour used everywhere in the lesson
                                 (DESIGN.md §2): federalist = navy, a woodtype "F", an eagle-seal doodle; anti = brick, a
                                 woodtype "A", a scroll-of-rights doodle → {color, ink, letter, label, short, doodle}
   ========================================================================== */
(function () {
  const C = {
    navy: '#1f2f55', brick: '#9e2b25', cream: '#f3ead7', gold: '#b08a2e',
    parchment: '#efe2c2', wax: '#8e1f1b', waxHi: '#b8322b', ink: '#2b2420', kraft: '#c99a67',
  };
  const BRANCH = {
    legislative: { color: C.navy, ink: C.cream, building: 'capitol', tool: 'quill', label: 'LEGISLATIVE', job: 'makes the laws', short: 'MAKES IT' },
    executive:   { color: C.brick, ink: C.cream, building: 'whitehouse', tool: 'pen', label: 'EXECUTIVE', job: 'carries out the laws', short: 'RUNS IT' },
    judicial:    { color: C.gold, ink: C.ink, building: 'court', tool: 'gavel', label: 'JUDICIAL', job: 'judges the laws', short: 'JUDGES IT' },
  };
  const BUNT = [C.navy, C.cream, C.brick];

  /* fixed point lists (built once, so the engine caches their sprites) */
  const _pts = {};
  const pts = (key, make) => _pts[key] || (_pts[key] = Object.assign(make(), { k: 'civic:' + key }));
  const pennant = (w, h, seed) => pts(`pen${w},${h},${seed}`, () => {
    const r = rng(seed), j = () => (r() - 0.5) * 3;
    return [[-w / 2 + j(), j()], [w / 2 + j(), j()], [j(), h + j()]];
  });

  function bunting(x0, x1, y, { sag = 46, n = 13, seed = 1, t0 = -1e6, sway = 1, w = null } = {}) {
    const L = x1 - x0, pw = w || Math.min(96, (L / n) * 0.86), ph = pw * 1.05;
    // the string (a sagging curve)
    const line = [];
    for (let i = 0; i <= 24; i++) { const u = i / 24; line.push([x0 + L * u, y + Math.sin(u * Math.PI) * sag]); }
    ink(line, { w: 3, color: 'rgba(60,44,30,0.8)', seed: seed * 13, passes: 1, step: 40, amp: 0.6 });
    for (let i = 0; i < n; i++) {
      const u = (i + 0.5) / n, x = x0 + L * u, yy = y + Math.sin(u * Math.PI) * sag;
      const s = pop(T, t0 + i * 0.04, 0.3, 2);
      if (s <= 0) continue;
      const rot = Math.cos(u * Math.PI) * -0.18 + Math.sin(T * 1.3 + i * 1.7) * 0.03 * sway;
      paperPiece(pennant(Math.round(pw), Math.round(ph), seed * 31 + (i % 5)), x, yy - 2, BUNT[i % 3], { rot, s, seed: seed * 40 + (i % 7), depth: 0.7 });
    }
  }

  function seal(x, y, r, { text = '', sub = '', seed = 3, s = 1, rot = 0, ribbon = true } = {}) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    if (ribbon) {
      const tail = pts(`tail${Math.round(r)}`, () => [[-r * 0.18, 0], [r * 0.18, 0], [r * 0.26, r * 1.5], [0, r * 1.3], [-r * 0.1, r * 1.55]]);
      paperPiece(tail, -r * 0.35, r * 0.2, C.navy, { rot: 0.35, seed: seed + 1, depth: 0.6 });
      paperPiece(tail, r * 0.35, r * 0.2, C.brick, { rot: -0.3, seed: seed + 2, depth: 0.6 });
    }
    paperPiece(blobPts(Math.round(r), Math.round(r), seed + 5, 0.12, 28), 0, 0, C.wax, { seed: seed + 5, depth: 1 });
    paperCircle(0, 0, Math.round(r * 0.72), C.waxHi, { seed: seed + 6, depth: 0, rough: 0.02 });
    inkCircle(0, 0, r * 0.62, { w: 2.5, color: 'rgba(60,8,8,0.55)', seed: seed + 7, passes: 1 });
    if (text) {
      const ty = sub ? -r * 0.14 : 0;
      g.font = `${Math.round(r * (text.length > 2 ? 0.42 : 0.7))}px 'Alfa Slab One'`; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillStyle = 'rgba(70,10,10,0.75)'; g.fillText(text, 1.5, ty + 2.5);
      g.fillStyle = '#f0c9b5'; g.fillText(text, 0, ty);
      if (sub) {
        g.font = `${Math.round(r * 0.3)}px 'Alfa Slab One'`;
        g.fillStyle = 'rgba(70,10,10,0.75)'; g.fillText(sub, 1, r * 0.3 + 2);
        g.fillStyle = '#f0c9b5'; g.fillText(sub, 0, r * 0.3);
      }
    }
    g.restore();
  }

  /* woodtype: a letter per block, one font (Alfa Slab One), light rotation; the block + glyph bake once */
  const WOOD_FONT = "'Alfa Slab One'";
  function woodtype(str, x, y, size, { color = C.cream, ink: inkC = C.navy, t0 = -1e6, seed = 1, align = 'center', gap = 0.06, stagger = 0.045, rot = 0.03 } = {}) {
    const chars = [...String(str)];
    g.save(); g.font = `${size}px ${WOOD_FONT}`;
    const widths = chars.map(ch => (ch === ' ' ? size * 0.36 : g.measureText(ch).width + size * 0.2));
    g.restore();
    const total = widths.reduce((a, w) => a + w, 0) + gap * size * (chars.length - 1);
    let cx = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
    const r = rng(seed);
    chars.forEach((ch, i) => {
      const w = widths[i], px = cx + w / 2, jr = (r() - 0.5) * 2 * rot, jy = (r() - 0.5) * size * 0.06;
      cx += w + gap * size;
      if (ch === ' ') return;
      const s = pop(T, t0 + i * stagger, 0.3, 2.2);
      if (s <= 0) return;
      g.save(); g.translate(px, y + jy); g.rotate(jr); g.scale(s, s);
      paperRect(0, 0, Math.round(w), Math.round(size * 1.12), color, { seed: seed * 100 + i, depth: 0.6, boil: false });
      g.font = `${size}px ${WOOD_FONT}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = inkC;
      g.fillText(ch, 0, size * 0.05);
      g.restore();
    });
    return total;
  }

  function masthead(title, x, y, w, { date = '', price = '', t0 = -1e6, size = 96, color = C.ink } = {}) {
    const rule = (yy, k) => ink([[x - w / 2, yy], [x + w / 2, yy]], { w: k, color, seed: Math.round(yy), passes: 1, step: 60, amp: 0.5 });
    rule(y - size * 0.72, 5); rule(y - size * 0.72 + 10, 2);
    g.save(); g.font = `${size}px 'Abril Fatface'`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = color;
    const a = clamp((T - t0) / 0.3); g.globalAlpha *= a; g.fillText(title, x, y); g.restore();
    rule(y + size * 0.66, 2); rule(y + size * 0.66 + 10, 5);
    if (date || price) {
      g.save(); g.font = `30px 'Special Elite'`; g.fillStyle = color; g.textBaseline = 'middle';
      g.textAlign = 'left'; if (date) g.fillText(date, x - w / 2, y + size * 0.66 + 36);
      g.textAlign = 'right'; if (price) g.fillText(price, x + w / 2, y + size * 0.66 + 36);
      g.restore();
    }
  }

  function parchment(x, y, w, h, { seed = 1, rot = 0, lines = 0, depth = 1.4, color = C.parchment, s = 1 } = {}) {
    paperRect(x, y, Math.round(w), Math.round(h), color, { seed, rot, torn: true, depth, s, boil: false });
    if (lines && s > 0.99) {
      g.save(); g.translate(x, y); g.rotate(rot);
      for (let k = 0; k < lines; k++) {
        const yy = -h / 2 + (k + 1) * h / (lines + 1);
        ink([[-w / 2 + 40, yy], [w / 2 - 40, yy]], { w: 1.6, color: 'rgba(120,90,50,0.25)', seed: seed * 7 + k, passes: 1, step: 90, amp: 0.5 });
      }
      g.restore();
    }
  }

  /* a building: the cut-out when it's in (the assets agent's img/<kind>.png), else a paper stand-in */
  const BUILD_COLOR = { capitol: C.navy, whitehouse: C.brick, court: C.gold };
  function building(kind, x, y, h, { s = 1, rot = 0, seed = 5 } = {}) {
    if (s <= 0.001) return;
    if (IMG[kind]) { cut(kind, x, y, h, { ax: 0.5, ay: 1, s, rot, seed, depth: 1.2 }); return; }
    const col = BUILD_COLOR[kind] || C.navy, W = h * 1.25;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    const white = PAL.white, sk = Math.round(h);
    if (kind === 'capitol') {
      paperRect(0, -h * 0.16, Math.round(W), Math.round(h * 0.32), white, { seed: seed, depth: 1 });
      paperRect(0, -h * 0.4, Math.round(W * 0.36), Math.round(h * 0.18), white, { seed: seed + 1, depth: 0.6 });
      paperPiece(pts(`dome${sk}`, () => { const p = []; for (let i = 0; i <= 18; i++) { const a = Math.PI + i / 18 * Math.PI; p.push([Math.cos(a) * h * 0.2, Math.sin(a) * h * 0.26]); } return p; }), 0, -h * 0.49, white, { seed: seed + 2, depth: 0.6 });
      paperRect(0, -h * 0.8, Math.round(h * 0.05), Math.round(h * 0.1), white, { seed: seed + 3, depth: 0.3 });
      for (let i = -5; i <= 5; i++) ink([[i * W * 0.085, -h * 0.03], [i * W * 0.085, -h * 0.27]], { w: 3, color: col, seed: seed * 10 + i, passes: 1, step: 30 });
      for (let i = -2; i <= 2; i++) ink([[i * h * 0.07, -h * 0.34], [i * h * 0.07, -h * 0.47]], { w: 2.5, color: col, seed: seed * 20 + i, passes: 1, step: 30 });
    } else if (kind === 'whitehouse') {
      paperRect(0, -h * 0.3, Math.round(W), Math.round(h * 0.6), white, { seed, depth: 1 });
      paperPiece(pts(`pedi${sk}`, () => [[-h * 0.3, 0], [h * 0.3, 0], [0, -h * 0.16]]), 0, -h * 0.6, white, { seed: seed + 1, depth: 0.5 });
      for (let i = -2; i <= 2; i++) ink([[i * h * 0.12, -h * 0.02], [i * h * 0.12, -h * 0.58]], { w: 3.5, color: col, seed: seed * 10 + i, passes: 1, step: 30 });
      for (const sx of [-1, 1]) for (let k = 0; k < 2; k++) paperRect(sx * W * 0.36, -h * (0.18 + k * 0.24), Math.round(h * 0.09), Math.round(h * 0.13), col, { seed: seed + 4 + k + (sx > 0 ? 2 : 0), depth: 0 });
    } else {
      paperRect(0, -h * 0.04, Math.round(W), Math.round(h * 0.08), white, { seed, depth: 1 });
      paperRect(0, -h * 0.66, Math.round(W * 0.96), Math.round(h * 0.08), white, { seed: seed + 1, depth: 0.6 });
      paperPiece(pts(`court${sk}`, () => [[-W * 0.5, 0], [W * 0.5, 0], [0, -h * 0.24]]), 0, -h * 0.7, white, { seed: seed + 2, depth: 0.6 });
      for (let i = -3; i <= 3; i++) paperRect(i * W * 0.13, -h * 0.35, Math.round(h * 0.07), Math.round(h * 0.54), white, { seed: seed + 10 + i, depth: 0.4, outline: true });
      paperCircle(0, -h * 0.8, Math.round(h * 0.04), col, { seed: seed + 3, depth: 0 });
    }
    g.restore();
  }

  function tool(kind, x, y, h, { rot = 0, s = 1, seed = 9 } = {}) {
    if (s <= 0.001) return;
    if (IMG[kind]) { cut(kind, x, y, h, { rot, s, seed, depth: 0.9 }); return; }
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    if (kind === 'gavel') {
      paperRect(0, h * 0.1, Math.round(h * 0.12), Math.round(h * 0.7), C.kraft, { seed, depth: 0.6 });
      paperRect(0, -h * 0.3, Math.round(h * 0.7), Math.round(h * 0.28), '#7a4e2a', { seed: seed + 1, depth: 0.8 });
    } else if (kind === 'quill') {
      paperPiece(pts(`quill${Math.round(h)}`, () => [[0, h * 0.5], [h * 0.12, -h * 0.1], [h * 0.02, -h * 0.5], [-h * 0.1, -h * 0.1]]), 0, 0, PAL.white, { seed, depth: 0.6, outline: true });
      ink([[0, h * 0.5], [h * 0.02, -h * 0.45]], { w: 2.5, seed: seed + 2, passes: 1 });
    } else {
      paperRect(0, 0, Math.round(h * 0.12), Math.round(h * 0.8), C.ink, { seed, depth: 0.6 });
      paperPiece(pts(`nib${Math.round(h)}`, () => [[-h * 0.06, 0], [h * 0.06, 0], [0, h * 0.14]]), 0, h * 0.4, C.gold, { seed: seed + 1, depth: 0 });
    }
    g.restore();
  }

  /* ---------------- free-speech: the five freedoms ---------------- */
  C.plum = '#6b3a6b'; C.green = '#2e5a4f'; C.flame = '#e8b64a';
  const FREEDOMS = ['religion', 'speech', 'press', 'assembly', 'petition'];
  const FREEDOM = {
    religion: { color: C.plum, ink: C.cream, label: 'RELIGION', icon: 'candle' },
    speech:   { color: C.navy, ink: C.cream, label: 'SPEECH', icon: 'bubble' },
    press:    { color: C.brick, ink: C.cream, label: 'PRESS', icon: 'newspaper' },
    assembly: { color: C.green, ink: C.cream, label: 'ASSEMBLY', icon: 'people' },
    petition: { color: C.gold, ink: C.ink, label: 'PETITION', icon: 'quill' },
  };
  Object.values(FREEDOM).forEach(Object.freeze);

  /* an icon drawn about (0, 0), height ≈ size; every shape is a cached point list keyed by its rounded size */
  function icon(kind, x, y, size, { s = 1, rot = 0, seed = 11, color = C.cream } = {}) {
    if (s <= 0.001) return;
    const u = Math.round(size), K = `${kind}${u}`;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    if (kind === 'candle') {
      paperRect(0, u * 0.16, Math.round(u * 0.26), Math.round(u * 0.56), color, { seed, depth: 0.5, boil: false });
      paperRect(0, u * 0.45, Math.round(u * 0.5), Math.round(u * 0.08), color, { seed: seed + 1, depth: 0.4, boil: false });
      paperPiece(pts('flame' + K, () => [[0, -u * 0.5], [u * 0.1, -u * 0.26], [u * 0.07, -u * 0.15], [-u * 0.07, -u * 0.15], [-u * 0.1, -u * 0.26]]), 0, 0, C.flame, { seed: seed + 2, depth: 0.3 });
      ink([[0, -u * 0.15], [0, -u * 0.1]], { w: 2, color: C.ink, seed: seed + 3, passes: 1 });
    } else if (kind === 'bubble') {
      paperPiece(blobPts(Math.round(u * 0.46), Math.round(u * 0.32), 5, 0.02, 30), 0, -u * 0.06, color, { seed, depth: 0.5, boil: false });
      paperPiece(pts('tail' + K, () => [[-u * 0.2, u * 0.16], [-u * 0.02, u * 0.2], [-u * 0.3, u * 0.42]]), 0, 0, color, { seed: seed + 1, depth: 0.3, boil: false });
      for (let k = -1; k <= 1; k++) paperCircle(k * u * 0.17, -u * 0.06, Math.max(2, Math.round(u * 0.055)), C.ink, { seed: seed + 4 + k, depth: 0 });
    } else if (kind === 'newspaper') {
      paperRect(0, 0, Math.round(u * 0.82), Math.round(u * 0.66), color, { seed, depth: 0.5, rot: -0.04, boil: false });
      g.save(); g.rotate(-0.04);
      paperRect(0, -u * 0.2, Math.round(u * 0.66), Math.round(u * 0.12), C.ink, { seed: seed + 1, depth: 0, boil: false });
      for (let k = 0; k < 3; k++) {
        ink([[-u * 0.33, -u * 0.02 + k * u * 0.12], [-u * 0.04, -u * 0.02 + k * u * 0.12]], { w: 2.2, color: C.ink, seed: seed + 5 + k, passes: 1, step: 20, amp: 0.4 });
        ink([[u * 0.04, -u * 0.02 + k * u * 0.12], [u * 0.33, -u * 0.02 + k * u * 0.12]], { w: 2.2, color: C.ink, seed: seed + 9 + k, passes: 1, step: 20, amp: 0.4 });
      }
      g.restore();
    } else if (kind === 'people') {
      const shoulder = pts('sh' + K, () => { const p = []; for (let i = 0; i <= 14; i++) { const a = Math.PI + i / 14 * Math.PI; p.push([Math.cos(a) * u * 0.17, Math.sin(a) * u * 0.2]); } return p; });
      for (const [dx, dy, k] of [[-u * 0.25, u * 0.06, 0], [u * 0.25, u * 0.06, 1], [0, 0, 2]]) {
        paperCircle(dx, dy - u * 0.2, Math.round(u * 0.1), color, { seed: seed + k, depth: 0.4, boil: false });
        paperPiece(shoulder, dx, dy + u * 0.2, color, { seed: seed + 3 + k, depth: 0.4, boil: false });
      }
    } else {                                  // quill + scroll
      paperRect(-u * 0.06, u * 0.12, Math.round(u * 0.62), Math.round(u * 0.42), color, { seed, depth: 0.5, rot: 0.05, boil: false });
      paperCircle(-u * 0.37, u * 0.12, Math.round(u * 0.08), color, { seed: seed + 1, depth: 0.3 });
      paperCircle(u * 0.25, u * 0.12, Math.round(u * 0.08), color, { seed: seed + 2, depth: 0.3 });
      paperPiece(pts('qf' + K, () => [[u * 0.02, u * 0.2], [u * 0.3, -u * 0.36], [u * 0.36, -u * 0.5], [u * 0.2, -u * 0.34], [u * 0.08, -u * 0.08]]), 0, 0, PAL.white, { seed: seed + 3, depth: 0.4, outline: true });
      ink([[u * 0.02, u * 0.2], [u * 0.3, -u * 0.4]], { w: 2, color: C.ink, seed: seed + 4, passes: 1 });
    }
    g.restore();
  }

  function freedom(id, x, y, size, { s = 1, rot = 0, seed = 21, label = false, t0 = -1e6 } = {}) {
    const F = FREEDOM[id];
    if (!F || s <= 0.001) return;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperCircle(0, 0, Math.round(size * 0.5), F.color, { seed, depth: 1 });
    inkCircle(0, 0, size * 0.43, { w: 2, color: 'rgba(243,234,215,0.55)', seed: seed + 1, passes: 1 });
    icon(F.icon, 0, 0, size * 0.62, { seed: seed + 2, color: F.ink === C.ink ? C.cream : F.ink });
    g.restore();
    if (label) {
      // a printed tag under the badge: slab type on a paper strip in the freedom's colour, shrunk to the badge's width
      const ls = pop(T, t0, 0.3, 2.2) * s;
      if (ls <= 0.001) return;
      let fs = Math.round(size * 0.17);
      g.save(); g.font = `${fs}px 'Alfa Slab One'`;
      while (fs > 12 && g.measureText(F.label).width > size * 1.05) { fs -= 1; g.font = `${fs}px 'Alfa Slab One'`; }
      const w = Math.round(g.measureText(F.label).width + fs * 0.9), ty = y + size * 0.5 * s + fs * 1.1;
      g.translate(x, ty); g.rotate(-rot * 0.6); g.scale(ls, ls);
      paperRect(0, 0, w, Math.round(fs * 1.5), F.color, { seed: seed + 7, depth: 0.5, boil: false });
      g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = F.ink; g.fillText(F.label, 0, fs * 0.06);
      g.restore();
    }
  }

  /* ---------------- free-speech: the engraving props (IMG when in, else paper stand-ins) ---------------- */
  function prop(kind, x, y, h, { s = 1, rot = 0, seed = 31, ax = 0.5, ay = 0.5, flip = false } = {}) {
    if (s <= 0.001) return;
    if (IMG[kind]) { cut(kind, x, y, h, { ax, ay, s, rot, seed, flip, depth: 1 }); return; }
    const u = Math.round(h), K = `${kind}${u}`;
    g.save(); g.translate(x + (0.5 - ax) * h, y + (0.5 - ay) * h); g.rotate(rot); g.scale(s * (flip ? -1 : 1), s);
    if (kind === 'armband') {
      paperRect(0, 0, Math.round(u * 0.9), Math.round(u * 0.34), '#1d1b1a', { seed, depth: 0.8, rot: -0.06 });
      ink([[-u * 0.42, -u * 0.12], [u * 0.42, -u * 0.17]], { w: 2, color: 'rgba(255,255,255,0.25)', seed: seed + 1, passes: 1 });
      paperPiece(pts('knot' + K, () => [[0, 0], [u * 0.12, u * 0.28], [-u * 0.02, u * 0.3]]), u * 0.3, u * 0.1, '#1d1b1a', { seed: seed + 2, depth: 0.5 });
    } else if (kind === 'newspaper') {
      paperRect(0, 0, Math.round(u * 0.74), u, PAL.white, { seed, depth: 1, tex: 'tex_news' });
      paperRect(0, -u * 0.38, Math.round(u * 0.62), Math.round(u * 0.1), C.ink, { seed: seed + 1, depth: 0, boil: false });
      for (let k = 0; k < 8; k++) ink([[-u * 0.3, -u * 0.2 + k * u * 0.07], [u * 0.3, -u * 0.2 + k * u * 0.07]], { w: 2, color: 'rgba(43,36,32,0.45)', seed: seed + 3 + k, passes: 1, step: 30, amp: 0.4 });
    } else if (kind === 'megaphone') {
      paperPiece(pts('horn' + K, () => [[-u * 0.4, -u * 0.1], [u * 0.3, -u * 0.36], [u * 0.3, u * 0.36], [-u * 0.4, u * 0.1]]), 0, 0, C.brick, { seed, depth: 1 });
      paperRect(u * 0.32, 0, Math.round(u * 0.08), Math.round(u * 0.74), C.cream, { seed: seed + 1, depth: 0.4 });
      paperRect(-u * 0.28, u * 0.22, Math.round(u * 0.1), Math.round(u * 0.3), C.ink, { seed: seed + 2, depth: 0.5 });
    } else if (kind === 'protest_sign') {
      paperRect(0, u * 0.18, Math.round(u * 0.06), Math.round(u * 0.64), C.kraft, { seed, depth: 0.6 });
      paperRect(0, -u * 0.2, Math.round(u * 0.7), Math.round(u * 0.46), PAL.white, { seed: seed + 1, depth: 1, rot: -0.03 });
    } else if (kind === 'scroll_rights') {
      paperRect(0, 0, Math.round(u * 0.62), u, C.parchment, { seed, depth: 1 });
      paperRect(0, -u * 0.5, Math.round(u * 0.72), Math.round(u * 0.1), '#d8c393', { seed: seed + 1, depth: 0.7 });
      paperRect(0, u * 0.5, Math.round(u * 0.72), Math.round(u * 0.1), '#d8c393', { seed: seed + 2, depth: 0.7 });
      for (let k = 0; k < 10; k++) ink([[-u * 0.24, -u * 0.36 + k * u * 0.075], [u * 0.24, -u * 0.36 + k * u * 0.075]], { w: 1.8, color: 'rgba(60,44,30,0.5)', seed: seed + 3 + k, passes: 1, step: 24, amp: 0.5 });
    } else if (kind === 'microphone') {
      paperRect(0, u * 0.2, Math.round(u * 0.06), Math.round(u * 0.6), C.ink, { seed, depth: 0.5 });
      paperPiece(blobPts(Math.round(u * 0.14), Math.round(u * 0.2), 3, 0.02, 24), 0, -u * 0.26, '#6f6a64', { seed: seed + 1, depth: 0.8 });
      paperRect(0, u * 0.48, Math.round(u * 0.34), Math.round(u * 0.06), C.ink, { seed: seed + 2, depth: 0.5 });
    } else if (kind === 'printing_press') {
      paperRect(0, u * 0.2, Math.round(u * 1.1), Math.round(u * 0.3), '#5a4632', { seed, depth: 1 });
      paperRect(-u * 0.4, -u * 0.12, Math.round(u * 0.08), Math.round(u * 0.66), '#5a4632', { seed: seed + 1, depth: 0.7 });
      paperRect(u * 0.4, -u * 0.12, Math.round(u * 0.08), Math.round(u * 0.66), '#5a4632', { seed: seed + 2, depth: 0.7 });
      paperRect(0, -u * 0.42, Math.round(u * 0.9), Math.round(u * 0.08), '#5a4632', { seed: seed + 3, depth: 0.7 });
      paperRect(0, -u * 0.06, Math.round(u * 0.6), Math.round(u * 0.1), C.ink, { seed: seed + 4, depth: 0.5 });
      paperRect(0, u * 0.02, Math.round(u * 0.56), Math.round(u * 0.05), PAL.white, { seed: seed + 5, depth: 0.2 });
    } else {
      paperRect(0, 0, u, u, C.parchment, { seed, depth: 0.6 });
    }
    g.restore();
  }

  /* ---------------- free-speech: a court docket sheet (case cards; the video's case callouts) ---------------- */
  function docket(x, y, w, h, { name = '', year = '', vote = '', eoc = false, lines = [], s = 1, rot = 0, seed = 41, t0 = -1e6 } = {}) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    parchment(0, 0, w, h, { seed, depth: 1.3, lines: 0 });
    // ruled margin + DOCKET rule
    ink([[-w / 2 + 46, -h / 2 + 24], [-w / 2 + 46, h / 2 - 24]], { w: 2, color: 'rgba(158,43,37,0.45)', seed: seed + 1, passes: 1, step: 60, amp: 0.4 });
    g.font = `${Math.round(Math.min(34, h * 0.08))}px 'Special Elite'`; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillStyle = C.brick;
    g.fillText('DOCKET', -w / 2 + 64, -h / 2 + 40);
    ink([[-w / 2 + 64, -h / 2 + 62], [w / 2 - 30, -h / 2 + 62]], { w: 2.5, color: C.ink, seed: seed + 2, passes: 1, step: 60, amp: 0.4 });
    // the case name (slab type, shrunk to fit beside the seal)
    const r = Math.min(h * 0.16, w * 0.11), maxW = w - 64 - 30 - r * 2.4;
    let fs = Math.round(Math.min(56, h * 0.13));
    g.font = `${fs}px 'Alfa Slab One'`;
    while (fs > 18 && g.measureText(name).width > maxW) { fs -= 2; g.font = `${fs}px 'Alfa Slab One'`; }
    g.fillStyle = C.ink; g.fillText(name, -w / 2 + 64, -h / 2 + 62 + fs * 0.95);
    // body lines (typewriter)
    const ls = Math.round(Math.min(30, h * 0.07));
    g.font = `${ls}px 'Special Elite'`; g.fillStyle = C.ink;
    lines.forEach((l, i) => g.fillText(String(l), -w / 2 + 64, -h / 2 + 62 + fs * 1.9 + i * ls * 1.4));
    // the seal (year over vote, top right) and the EOC badge (bottom right) sit in the sheet's own frame, so they
    // follow its position, rotation AND scale `s`
    seal(w / 2 - r * 1.3, -h / 2 + r * 1.35, r, { text: String(year), sub: String(vote).replace('-', '–'), seed: seed + 5, s: pop(T, t0, 0.3, 2.4), rot: -0.12, ribbon: false });
    if (eoc) {
      g.save(); g.translate(w / 2 - 130, h / 2 - 34); g.rotate(-0.06);
      paperRect(0, 0, 200, 44, C.navy, { seed: seed + 8, depth: 0.6, boil: false });
      g.font = "22px 'Alfa Slab One'"; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = C.cream; g.fillText('ON THE EOC', 0, 2);
      g.restore();
    }
    g.restore();
  }

  const SIDE = {
    federalist: { color: C.navy, ink: C.cream, letter: 'F', label: 'FEDERALISTS', short: 'FEDERALIST', doodle: 'eagle' },
    anti:       { color: C.brick, ink: C.cream, letter: 'A', label: 'ANTI-FEDERALISTS', short: 'ANTI-FEDERALIST', doodle: 'scroll' },
  };
  Object.values(SIDE).forEach(Object.freeze);

  window.Civic = { C, BRANCH, bunting, seal, woodtype, masthead, parchment, building, tool,
    FREEDOM, FREEDOMS, icon, freedom, prop, docket, SIDE };
})();
